import Button from "@/comps/button"
import { ErrorMessage } from "@/comps/message"
import { SizedSkeleton } from "@/comps/skeleton"
import { SquareSwitch } from "@/comps/toggle"
import {
	PaymentSessionData,
	PAYMENT_DATA_KEY,
} from "@/utils/adjust"
import { assetUrl } from "@/utils/cdn"
import { useAuth } from "@/utils/client-auth"
import { PriceOptionProps } from "@/utils/coins"
import { redirectToLogin } from "@/utils/login-redirect"
import { NotificationContext } from "@/utils/notification"
import axios from "axios"
import clsx from "clsx"
import { useTranslation } from "next-i18next"
import { useRouter } from "next/router"
import { PaymentCoinPacksResponse } from "pages/api/payment-coin-packs"
import {
	PaymentPackUrlRequest,
	PaymentPackUrlResponse,
} from "pages/api/payment-pack-url"
import { Package } from "pages/api/pricing-packs"
import { useContext, useEffect, useState } from "react"
import { useQuery } from "react-query"
import { v4 } from "uuid"
import RenderBlock from "./render-block"

export interface Variant {
	pack_id: string
	monthly_tokens: number
	yearly_tokens: number
	monthly_price: number
	yearly_price: number
}
interface Plan {
	id: string
	subtitle?: string
	contact_url?: string
	points: string[]
	variants?: Variant[]
}

export type PlansType = {
	free: Plan
	basic: Plan
	advanced: Plan
}

interface PricingOrCoinProps {
	activeState: "pricing" | "coin"
	isYearly: boolean
	setIsMonthly: (v: boolean) => void
	plans: PlansType
	savePercent: number
	handlePurchaseClick: (packId: string) => void
	basic: Package
	advanced: Package
}

async function fetchPrices() {
	return await axios
		.get<PaymentCoinPacksResponse>(
			"/api/payment-coin-packs",
		)
		.then((res) => res.data)
}

export default function PricingOrCoin(
	props: PricingOrCoinProps,
) {
	const {
		activeState,
		isYearly,
		setIsMonthly,
		plans,
		savePercent,
		handlePurchaseClick,
		basic,
		advanced,
	} = props
	const { t } = useTranslation()
	const [selected, setSelected] = useState<string | null>(
		null,
	)
	const [isProcessing, setIsProcessing] = useState(false)
	const { notify } = useContext(NotificationContext)
	const { userInfo } = useAuth()
	const router = useRouter()

	async function handlePurchase() {
		if (!selected) {
			return
		}

		setIsProcessing(true)

		const sessionId = v4()
		try {
			const payload: PaymentPackUrlRequest = {
				id: selected,
				location: router.asPath,
				sessionId,
			}

			const { url, value, currency } = await axios
				.post<PaymentPackUrlResponse>(
					"/api/payment-pack-url",
					payload,
				)
				.then((res) => res.data)

			const paymentData: PaymentSessionData = {
				value,
				currency,
				session: sessionId,
			}

			localStorage.setItem(
				PAYMENT_DATA_KEY,
				JSON.stringify(paymentData),
			)

			await router.push(url)
		} catch (e) {
			console.error(e)
			notify(
				<ErrorMessage>
					{t("txt_cannot_buy_now")}
				</ErrorMessage>,
			)
		}

		setIsProcessing(false)
	}

	function handleCoinsPurchase() {
		if (userInfo.isAnonymous) {
			redirectToLogin()
		} else {
			handlePurchase()
		}
	}

	const CACHE_MINUTES = 10

	const prices = useQuery({
		queryKey: ["coins", "prices", userInfo?.userId],
		queryFn: fetchPrices,
		enabled: userInfo !== null,
		staleTime: Infinity,
		cacheTime: CACHE_MINUTES * 60 * 1000,
	})

	useEffect(() => {
		if (!prices.data) {
			return
		}

		if (selected) {
			return
		}

		const popular = prices.data.find(
			(price) => price.popular,
		)

		if (popular) {
			setSelected(popular.id)
		}
	}, [prices, selected])

	switch (activeState) {
		case "pricing":
			return (
				<div
					className={clsx(
						"flex w-full flex-col items-center",
						"pt-[44px] tablet:pt-[30px] desktop:pt-[42px] ",
					)}>
					<div className=" flex gap-[10px]">
						<span
							className={clsx(
								isYearly
									? "text-blue-500"
									: "text-blue-900",
								"text-[18px] font-500 transition-colors",
							)}>
							{t("txt_monthly")}
						</span>
						<SquareSwitch
							onChange={setIsMonthly}
							enabled={isYearly}
						/>
						<span
							className={clsx(
								isYearly
									? "text-blue-900"
									: "text-blue-500",
								"text-[18px] font-500 transition-colors ",
							)}>
							{t("txt_yearly")}
						</span>
						<span
							className={clsx(
								"text-[18px] font-500 transition-colors",
								isYearly
									? "text-blue-900"
									: "text-blue-500",
							)}>
							{t("fs_save_percent", {
								percent: String(savePercent),
							})}
						</span>
					</div>

					<RenderBlock
						plans={plans}
						isYearly={isYearly}
						handlePurchaseClick={handlePurchaseClick}
						basic={basic}
						advanced={advanced}
					/>

					<p
						className={clsx(
							"pt-4 text-[16px] font-400 text-blue-500 transition-opacity",
							isYearly ? "opacity-100" : "opacity-0",
						)}>
						*{t("txt_billed_yearly")}
					</p>
				</div>
			)
		case "coin":
			return (
				<div className=" flex w-full flex-col items-center gap-[32px] pt-[62px]">
					<section
						className={clsx(
							"grid w-full max-w-[344px] grid-cols-2 px-[20px] tablet:max-w-none",
							" tablet:w-[582px] desktop:w-[775px] desktop:grid-cols-3 desktop:px-0",
						)}>
						{prices.data?.map((price) => (
							<PriceOption
								key={price.id}
								{...price}
								selected={price.id === selected}
								onSelected={() => {
									if (!userInfo || userInfo.isAnonymous) {
										setSelected(price.id)
										redirectToLogin()
										return
									}
									if (selected === price.id) {
										handlePurchase()
									} else {
										setSelected(price.id)
									}
								}}
							/>
						))}
						{prices.isFetching && (
							<>
								<SizedSkeleton
									className={clsx(
										"h-[72px] flex-1 rounded-[4px] inner-border tablet:h-[145px] tablet:w-[200px]",
										" tablet:flex-none tablet:rounded-[12px]",
										"basis-1/4 bg-color-cell inner-border-blue-200",
									)}
								/>
								<SizedSkeleton
									className={clsx(
										"h-[72px] flex-1 rounded-[4px] inner-border tablet:h-[145px] tablet:w-[200px]",
										" tablet:flex-none tablet:rounded-[12px]",
										"basis-1/4 bg-color-cell inner-border-blue-200",
									)}
								/>
								<SizedSkeleton
									className={clsx(
										"h-[72px] flex-1 rounded-[4px] inner-border tablet:h-[145px] tablet:w-[200px]",
										" tablet:flex-none tablet:rounded-[12px]",
										"basis-1/4 bg-color-cell inner-border-blue-200",
									)}
								/>
								<SizedSkeleton
									className={clsx(
										"h-[72px] flex-1 rounded-[4px] inner-border tablet:h-[145px] tablet:w-[200px]",
										" tablet:flex-none tablet:rounded-[12px]",
										"basis-1/4 bg-color-cell inner-border-blue-200",
									)}
								/>
								<SizedSkeleton
									className={clsx(
										"h-[72px] flex-1 rounded-[4px] inner-border tablet:h-[145px] tablet:w-[200px]",
										"tablet:flex-none tablet:rounded-[12px]",
										"basis-1/4 bg-color-cell inner-border-blue-200",
									)}
								/>
								<SizedSkeleton
									className={clsx(
										"h-[72px] flex-1 rounded-[4px] inner-border tablet:h-[145px] tablet:w-[200px]",
										"tablet:flex-none tablet:rounded-[12px]",
										"basis-1/4 bg-color-cell inner-border-blue-200",
									)}
								/>
							</>
						)}
					</section>

					<Button
						text={t("txt_purchase_coins")}
						disabled={!prices.isSuccess || isProcessing}
						onClick={handleCoinsPurchase}
						primary
					/>
				</div>
			)
	}
}

export function PriceOption(props: PriceOptionProps) {
	const {
		coins,
		price,
		popular,
		best,
		selected,
		onSelected,
	} = props

	const { t } = useTranslation()

	const priceTag = (price / 100).toFixed(2)

	return (
		<button
			onClick={onSelected}
			className={clsx(
				"relative flex w-full flex-col items-center justify-center",
				"h-[110px] rounded-[4px] inner-border tablet:h-[148px] tablet:flex-none",
				selected
					? "bg-primary-100 inner-border-primary-500"
					: "bg-color-cell inner-border-blue-200",
				"basis-1/4 transition-all",
			)}>
			<div className="flex flex-col items-center">
				<div className="flex gap-1">
					<span className="text-[13px] font-700 text-blue-800 tablet:text-[22px]">
						{coins.toLocaleString()}
					</span>
					<img
						src={assetUrl("/general/coin.svg")}
						alt="coin icon"
						className="aspect-square pointer-events-none w-5"
					/>
				</div>

				<span
					className={clsx(
						"text-[12px] tablet:text-[18px]",
						selected ? "text-blue-800" : "text-blue-600",
					)}>
					${priceTag}
				</span>
			</div>

			{popular && (
				<div
					className={clsx(
						"absolute right-2 top-2",
						"rounded-[4px] tablet:rounded-[12px] tablet:rounded-tr-none",
						"px-1 tablet:px-4",
						"text-[8px] tablet:text-[10px]",
						"font-700 uppercase text-color-white",
						" bg-primary-500",
					)}>
					{t("lbl_popular")}
				</div>
			)}

			{best && (
				<div
					className={clsx(
						"absolute right-2 top-2 bg-primary-100",
						"rounded-[4px] tablet:rounded-[12px]",
						"px-1 tablet:px-4",
						"text-[8px] tablet:text-[10px]",
						"font-700 uppercase text-primary-500",
					)}>
					{t("lbl_best")}
				</div>
			)}
		</button>
	)
}
